import React, { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { Download, FileText, ImageIcon, Loader2, Send, Sparkles, Upload as UploadIcon, X } from "lucide-react";
import { ACTIVE_CUSTOMER_ID_CHANGED_EVENT } from "../../hooks/useMerchantStores";
import { downloadDataUrl, slugify } from "./imageCreationService";
import { EXTRACTION_PDF_MAX_BYTES, extractAllProductImages, productNameFrom, productNamesFrom, runImageExtraction } from "./imageExtractionService";
import { buildZip } from "./zipStore";

// Image Extraction — upload a PDF, say which product you want (e.g. "extract
// product image for Rico Rice from attached PDF and enhance them with sharper
// detail and a clean white background"). The product's own picture is taken
// from the PDF at its native resolution and ENHANCED (no new image is
// generated): solid background → pure white, sharper, upscaled if small. Connected to the Image Creation card by
// WorkflowSection through onSendToImageCreation / pendingImages, like Menu Data
// Extraction → Image Creation. Results live in this card only; nothing is saved.

const MAX_PROMPT = 2000;
const MAX_SENT_PRODUCTS = 30; // products searched for in one run (the server's limit)
const EXAMPLE_PROMPT =
  "Extract product image for Rico Rice from attached PDF and enhance them with sharper detail and a clean white background";

const inputCls =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-gray-50";

// Grey checkerboard behind each picture so transparent areas are visible.
const CHECKERBOARD_STYLE = {
  backgroundColor: "#ffffff",
  backgroundImage:
    "linear-gradient(45deg,#e5e7eb 25%,transparent 25%),linear-gradient(-45deg,#e5e7eb 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e5e7eb 75%),linear-gradient(-45deg,transparent 75%,#e5e7eb 75%)",
  backgroundSize: "12px 12px",
  backgroundPosition: "0 0,0 6px,6px -6px,-6px 0",
};

const revokeAll = (list) => (list || []).forEach((p) => p?.url && URL.revokeObjectURL(p.url));

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Couldn't read an image."));
    reader.readAsDataURL(blob);
  });

const sameFile = (a, b) => !!a && !!b && a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

export default function ImageExtraction({
  onRemove,
  onSendToImageCreation,
  hasImageCreationCard,
  pendingProducts = [],
  onConsumePendingProduct,
  autoSendToNext = false, // Workflow tab: pass the found picture straight on to Image Creation
  onProgress, // Workflow tab: { type: "found" | "not-found" | "no-pdf" | "error", name, detail }
}) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [status, setStatus] = useState(""); // non-empty while processing
  const [results, setResults] = useState(null); // null = nothing run yet
  const [emptyMessage, setEmptyMessage] = useState("");
  const [missingNote, setMissingNote] = useState(""); // named products that had no picture, when others did
  const [foundIn, setFoundIn] = useState(""); // which PDF the pictures came from, when more than one was searched
  const [selected, setSelected] = useState({}); // index -> true
  // "Extract All Product Images": every product photo in the PDF, no names needed, no enhancing.
  const [allProducts, setAllProducts] = useState(null); // null = not run yet; each: { name, label, pageNumber, width, height, blob, url }
  const [allStatus, setAllStatus] = useState(""); // non-empty while that run is going
  const [allSelected, setAllSelected] = useState({}); // index -> true
  const [allNote, setAllNote] = useState("");
  const [isZipping, setIsZipping] = useState(false);
  const processing = !!status || !!allStatus;
  // Price / description of the menu item being looked up, so they travel on to Image Creation with the picture.
  const detailsRef = useRef(new Map()); // product name (lower case) -> { price, description }

  // Object URLs for the exact PDF images live as long as their results do.
  const resultsRef = useRef(null);
  useEffect(() => {
    resultsRef.current = results;
  }, [results]);
  const revokeUrls = (list) => (list || []).forEach((r) => r?.exact?.url && URL.revokeObjectURL(r.exact.url));
  const releaseExact = () => revokeUrls(resultsRef.current);
  useEffect(() => () => revokeUrls(resultsRef.current), []);

  const allRef = useRef(null);
  useEffect(() => {
    allRef.current = allProducts;
  }, [allProducts]);
  const releaseAll = () => revokeAll(allRef.current);
  useEffect(() => () => revokeAll(allRef.current), []);
  const clearAll = () => {
    releaseAll();
    setAllProducts(null);
    setAllSelected({});
    setAllNote("");
  };

  // A different store means different context; start clean.
  useEffect(() => {
    const reset = () => {
      setResults(null);
      setSelected({});
      setEmptyMessage("");
      setMissingNote("");
      revokeAll(allRef.current);
      setAllProducts(null);
      setAllSelected({});
      setAllNote("");
    };
    window.addEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, reset);
    return () => window.removeEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, reset);
  }, []);

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    if (picked.type !== "application/pdf") {
      message.error("Please choose a PDF file.");
      return;
    }
    if (picked.size > EXTRACTION_PDF_MAX_BYTES) {
      message.error("The PDF must be 25 MB or smaller.");
      return;
    }
    setFile(picked);
    releaseExact();
    setResults(null);
    setSelected({});
    setEmptyMessage("");
    clearAll();
  };

  // Looks for the product(s) in each PDF in turn. For a single request it stops at the first PDF that has a picture.
  // single: the request is exactly one product (e.g. a menu item), so "and" / "&" in its name is not a list.
  // names: the exact list of products (several menu items sent together). Products one PDF doesn't have are
  // searched for in the next PDF, and the pictures from all PDFs are shown together.
  const runSearch = async (sources, request, { auto = false, single = false, names = null } = {}) => {
    setResults(null);
    setSelected({});
    setEmptyMessage("");
    setMissingNote("");
    setFoundIn("");
    releaseExact();
    const errors = [];
    const sameName = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
    const progressName = names && names.length > 1 ? `${names.length} products` : names?.[0] || productNameFrom(request);
    let collected = [];
    const usedSources = [];
    let toFind = names; // names still without a picture (several-products mode only)

    const finish = (found, missing) => {
      setResults(found);
      setMissingNote(missing.length ? `No picture found for ${missing.map((n) => `“${n}”`).join(", ")}${sources.length > 1 ? "" : " in this PDF"}.` : "");
      setFoundIn(sources.length > 1 ? usedSources.join(" + ") : "");
      const preselected = {};
      found.forEach((r, i) => {
        if (r.enhancedDataUrl && r.verified !== "unsure") preselected[i] = true; // a picture that couldn't be confirmed is never ticked for you
      });
      setSelected(preselected);
      onProgress?.({ type: "found", name: progressName, detail: usedSources.join(", ") });
      // One picture per named product (the best one), in the order the customer named them.
      const bestPerItem = [];
      const seenItems = new Set();
      found.forEach((r) => {
        const key = String(r.item || r.label || "").toLowerCase();
        if (r.enhancedDataUrl && r.verified !== "unsure" && !seenItems.has(key)) {
          seenItems.add(key);
          bestPerItem.push(r);
        }
      });
      const unsureOnly = found.filter((r) => r.enhancedDataUrl && r.verified === "unsure" && !seenItems.has(String(r.item || r.label || "").toLowerCase()));
      if (auto && unsureOnly.length) {
        message.warning(`Couldn't confirm the picture for ${unsureOnly.map((r) => `“${r.label}”`).join(", ")}, so it wasn't sent on automatically. Check it in Image Extraction.`);
      }
      if (auto && bestPerItem.length && onSendToImageCreation && hasImageCreationCard) {
        onSendToImageCreation(
          bestPerItem.map((r, i) => ({ key: `extracted-${Date.now()}-${i}`, name: r.label || progressName, dataUrl: r.enhancedDataUrl, ...detailsFor(r.item || r.label) }))
        );
        message.success(bestPerItem.length === 1 ? "Found the picture and sent it to Image Creation." : `Found ${bestPerItem.length} pictures and sent them to Image Creation.`);
      }
    };

    try {
      for (let i = 0; i < sources.length; i++) {
        const source = sources[i];
        try {
          const label = sources.length > 1 ? ` in ${source.name}` : "";
          const { results: found, missing = [] } = await runImageExtraction({
            file: source,
            prompt: toFind ? `Extract product image for ${toFind.join("; ")}` : request,
            single,
            products: toFind || undefined,
            onStatus: (s) => setStatus(`${s}${label}`),
          });
          if (found.length === 0) continue;
          collected = [...collected, ...found];
          usedSources.push(source.name);
          if (names) {
            const have = collected;
            toFind = names.filter((n) => !have.some((r) => sameName(r.item, n)));
            if (toFind.length && i < sources.length - 1) continue; // look for the rest in the next PDF
            return finish(collected, toFind);
          }
          return finish(collected, missing);
        } catch (err) {
          errors.push(err.message || "Couldn't read that PDF.");
        }
      }
      if (collected.length) return finish(collected, names ? names.filter((n) => !collected.some((r) => sameName(r.item, n))) : []);
      // Not found anywhere.
      if (errors.length === sources.length) {
        onProgress?.({ type: "error", name: progressName, detail: errors[0] });
        return message.error(errors[0]);
      }
      const pdfNames = sources.map((s) => `“${s.name}”`).join(" or ");
      setResults([]);
      const asked = names || (single ? [productNameFrom(request)] : productNamesFrom(request));
      setEmptyMessage(
        `We couldn't find an image of ${asked.map((n) => `“${n}”`).join(" or ")} in ${pdfNames}. Check the product ${asked.length > 1 ? "names" : "name"}, or upload the PDF that contains ${asked.length > 1 ? "them" : "it"}.`
      );
      onProgress?.({ type: "not-found", name: progressName, detail: sources.map((s) => s.name).join(", ") });
    } finally {
      setStatus("");
    }
  };

  const handleExtract = async () => {
    const request = prompt.trim();
    if (!file) return message.warning("Upload a PDF first.");
    if (!request) return message.warning("Tell me which product to extract.");
    await runSearch([file], request);
  };

  // price / description of the menu item, when this search came from Menu Data Extraction for the same product
  const detailsFor = (name) => {
    const d = detailsRef.current.get(String(name || "").trim().toLowerCase());
    if (!d) return {};
    return { ...(d.price !== "" && d.price != null ? { price: d.price } : {}), ...(d.description ? { description: d.description } : {}) };
  };

  // Products sent from Menu Data Extraction (one, or several ticked together): search this card's PDF and the
  // menu's PDF (if it is one) for ALL of them in one run, so the results sit side by side.
  useEffect(() => {
    if (pendingProducts.length === 0 || processing) return;
    const batch = pendingProducts.slice();
    batch.forEach(() => onConsumePendingProduct?.());
    const seen = new Set();
    let names = [];
    detailsRef.current = new Map();
    batch.forEach((item) => {
      const name = String(item.name || "").trim().slice(0, 120);
      const key = name.toLowerCase();
      if (!name || seen.has(key)) return;
      seen.add(key);
      names.push(name);
      detailsRef.current.set(key, { price: item.price ?? "", description: item.description || "" });
    });
    if (!names.length) return;
    if (names.length > MAX_SENT_PRODUCTS) {
      message.warning(`Image Extraction searches for up to ${MAX_SENT_PRODUCTS} products at a time, so the first ${MAX_SENT_PRODUCTS} were used. Send the rest again afterwards.`);
      names = names.slice(0, MAX_SENT_PRODUCTS);
    }
    const label = names.length === 1 ? `“${names[0]}”` : `${names.length} products`;
    setPrompt(names.join(", ").slice(0, MAX_PROMPT));
    // The items come from the menu, so their pictures are in the menu's own PDF: search that one first and show
    // it in this card. A PDF already loaded here is only a fallback for items the menu PDF has no picture of.
    const menuPdf = batch.find((item) => item.file)?.file || null;
    const sources = [];
    if (menuPdf) sources.push(menuPdf);
    if (file && !(menuPdf && sameFile(file, menuPdf))) sources.push(file);
    if (menuPdf && !sameFile(file, menuPdf)) {
      setFile(menuPdf);
      clearAll(); // product pictures listed for the previous PDF no longer belong to the file shown
    }
    if (sources.length === 0) {
      message.info(`${label} received from Menu Data Extraction. Upload the PDF that has ${names.length === 1 ? "its picture" : "their pictures"}, then press Extract & Enhance.`);
      onProgress?.({ type: "no-pdf", name: names.length === 1 ? names[0] : `${names.length} products` });
      return;
    }
    message.info(`Looking for ${label} in ${sources.map((s) => s.name).join(" and ")}…`);
    // a single menu item is one product, however its name is spelled; several are searched as an exact list
    runSearch(sources, names.join(", "), names.length === 1 ? { auto: autoSendToNext, single: true } : { auto: autoSendToNext, names });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingProducts, processing]);

  const handleExtractAll = async () => {
    if (!file) return message.warning("Upload a PDF first.");
    clearAll();
    releaseExact();
    setResults(null);
    setSelected({});
    setEmptyMessage("");
    setMissingNote("");
    setAllStatus("Scanning the PDF…");
    try {
      const { products, scanned, unchecked } = await extractAllProductImages(file, { onStatus: setAllStatus });
      const withUrls = products.map((p) => ({ ...p, url: URL.createObjectURL(p.blob) }));
      setAllProducts(withUrls);
      const all = {};
      withUrls.forEach((_, i) => {
        all[i] = true;
      });
      setAllSelected(all);
      const notes = [];
      if (!withUrls.length) notes.push(scanned ? "No product pictures were found in this PDF — the pictures in it look like logos, banners or other artwork." : "No separate pictures were found in this PDF. Its pages may be flat scans.");
      if (unchecked) notes.push(`${unchecked} picture${unchecked === 1 ? "" : "s"} couldn't be checked, so ${unchecked === 1 ? "it was" : "they were"} left out. Run it again to retry.`);
      setAllNote(notes.join(" "));
      if (withUrls.length) message.success(`Found ${withUrls.length} product image${withUrls.length === 1 ? "" : "s"}.`);
    } catch (err) {
      console.error("Extract all products failed:", err);
      message.error(err?.message || "Couldn't extract the product images.");
    } finally {
      setAllStatus("");
    }
  };

  const baseFileName = () => String(file?.name || "products").replace(/\.pdf$/i, "").replace(/[^\w.-]+/g, "-").replace(/^-+|-+$/g, "") || "products";

  const handleDownloadZip = async (list, suffix) => {
    if (!list.length || isZipping) return;
    setIsZipping(true);
    try {
      const zip = await buildZip(list.map(({ name, blob }) => ({ name, blob })));
      const url = URL.createObjectURL(zip);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseFileName()}-${suffix}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("ZIP error:", err);
      message.error("Couldn't create the ZIP file. Try downloading the images one by one.");
    } finally {
      setIsZipping(false);
    }
  };

  const allSelectedCount = Object.values(allSelected).filter(Boolean).length;

  const handleSendAllSelected = async () => {
    const chosen = (allProducts || []).filter((_, i) => allSelected[i]);
    if (!chosen.length) return message.warning("Select at least one image first.");
    try {
      const items = await Promise.all(
        chosen.map(async (p, i) => ({ key: `extracted-${Date.now()}-${i}`, name: p.label || "Extracted product", dataUrl: await blobToDataUrl(p.blob) }))
      );
      onSendToImageCreation?.(items);
      message.success(items.length === 1 ? "Sent to Image Creation." : `${items.length} images sent to Image Creation (loaded one at a time).`);
    } catch (err) {
      message.error(err?.message || "Couldn't send the images.");
    }
  };

  const selectedItems = () =>
    (results || [])
      .map((r, i) => ({ r, i }))
      .filter(({ r, i }) => selected[i] && r.enhancedDataUrl)
      .map(({ r, i }) => ({
        key: `extracted-${Date.now()}-${i}`,
        name: r.label || "Extracted product",
        dataUrl: r.enhancedDataUrl,
        ...detailsFor(r.item || r.label),
      }));

  const handleSend = () => {
    const items = selectedItems();
    if (!items.length) return message.warning("Select at least one enhanced image first.");
    onSendToImageCreation?.(items);
    message.success(
      items.length === 1 ? "Sent to Image Creation." : `${items.length} images sent to Image Creation (loaded one at a time).`
    );
  };

  const canSend = !!onSendToImageCreation && hasImageCreationCard;
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <article className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-50 text-indigo-600">
            <ImageIcon size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">Image Extraction</h3>
            <p className="text-xs text-gray-500">Pull a product photo out of a PDF and place it on a clean white background</p>
          </div>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={processing}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-400 disabled:opacity-50"
          >
            Remove from Components
          </button>
        )}
      </header>

      <div className="flex flex-col gap-5 p-4 sm:p-5">
        {/* ---------- Upload ---------- */}
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-gray-900">PDF</span>
          {file ? (
            <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-2.5">
              <div className="grid h-10 w-10 flex-none place-items-center rounded-lg bg-white text-indigo-600">
                <FileText size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{file.name}</p>
                <p className="text-xs text-gray-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  releaseExact();
                  setResults(null);
                  setSelected({});
                  setEmptyMessage("");
                  clearAll();
                }}
                disabled={processing}
                aria-label="Remove file"
                className="flex-none rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
              >
                <X size={15} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center transition-colors hover:border-indigo-400"
            >
              <UploadIcon size={22} className="text-indigo-600" />
              <span className="text-sm font-medium text-gray-900">Click to upload a PDF</span>
              <span className="text-xs text-gray-500">Up to 20 pages, 25 MB</span>
            </button>
          )}
          <input ref={fileInputRef} type="file" accept="application/pdf" hidden onChange={handleFileChange} />
        </div>

        {/* ---------- Request ---------- */}
        <div className="flex flex-col gap-2">
          <label htmlFor="image-extraction-prompt" className="text-sm font-medium text-gray-900">
            Which product?
          </label>
          <textarea
            id="image-extraction-prompt"
            rows={3}
            maxLength={MAX_PROMPT}
            value={prompt}
            disabled={processing}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Type a product name, or several separated by commas or “and”"
            className={`${inputCls} resize-y`}
          />
          <button
            type="button"
            disabled={processing}
            onClick={() => setPrompt(EXAMPLE_PROMPT)}
            className="self-start rounded-full border border-gray-200 px-3 py-1 text-left text-xs text-gray-600 hover:border-indigo-400 hover:text-indigo-600 disabled:opacity-50"
          >
            Use example: “{EXAMPLE_PROMPT.slice(0, 48)}…”
          </button>
          <p className="text-xs text-gray-500">
            The picture from your PDF is enhanced with AI — the same product with sharper detail, better lighting and a clean white background. It can take up to a minute per picture.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleExtract}
              disabled={processing || !file || !prompt.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {status ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {status || "Extract & Enhance"}
            </button>
            <button
              type="button"
              onClick={handleExtractAll}
              disabled={processing || !file}
              title="Find every product photo in the PDF, no names needed"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-indigo-200 px-4 py-2.5 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {allStatus ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}
              {allStatus || "Extract All Product Images"}
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Name one or more products above for enhanced pictures, or press “Extract All Product Images” to save every product photo in the PDF as its own image (logos, ads, banners and QR codes are skipped).
          </p>
          {allNote && <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{allNote}</p>}
        </div>

        {/* ---------- All product images ---------- */}
        {allProducts && allProducts.length > 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-900">
                {allProducts.length} product {allProducts.length === 1 ? "image" : "images"} found · {allSelectedCount} selected
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const every = allSelectedCount === allProducts.length;
                    const next = {};
                    allProducts.forEach((_, i) => {
                      next[i] = !every;
                    });
                    setAllSelected(next);
                  }}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  {allSelectedCount === allProducts.length ? "Select none" : "Select all"}
                </button>
                <button
                  type="button"
                  onClick={() => handleDownloadZip(allProducts, "products")}
                  disabled={isZipping}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isZipping ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                  {isZipping ? "Preparing ZIP…" : "Download All (ZIP)"}
                </button>
                <button
                  type="button"
                  onClick={handleSendAllSelected}
                  disabled={!canSend || allSelectedCount === 0}
                  title={canSend ? "" : "Add the Image Creation component to your Components first"}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Send size={13} /> Send selected to Image Creation
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  aria-label="Remove the extracted product images"
                  title="Remove the extracted product images"
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                  <X size={15} />
                </button>
              </div>
            </div>
            {!canSend && <p className="text-xs text-gray-500">Add the Image Creation component from the Library to send images there.</p>}

            <div className="grid max-h-[28rem] grid-cols-2 gap-2 overflow-y-auto rounded-xl border border-gray-200 p-2 sm:grid-cols-3 md:grid-cols-4">
              {allProducts.map((p, i) => (
                <div key={p.name} className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
                  <div className="relative h-28 overflow-hidden p-1.5" style={CHECKERBOARD_STYLE}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${p.label}`}
                      checked={!!allSelected[i]}
                      onChange={(e) => setAllSelected((prev) => ({ ...prev, [i]: e.target.checked }))}
                      className="absolute left-1.5 top-1.5 z-10 h-4 w-4"
                    />
                    <img src={p.url} alt={p.label} loading="lazy" className="h-full w-full object-contain" />
                  </div>
                  <div className="flex items-center gap-1.5 border-t border-gray-200 px-2 py-1.5">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium text-gray-900" title={p.label}>{p.label}</p>
                      <p className="text-[11px] text-gray-500">Page {p.pageNumber} · {p.width} × {p.height}px</p>
                    </div>
                    <a
                      href={p.url}
                      download={p.name}
                      aria-label={`Download ${p.name}`}
                      title="Download this image"
                      className="flex-none rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-indigo-600"
                    >
                      <Download size={14} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500">These are the exact pictures from your PDF at their original size — not enhanced. Use “Extract & Enhance” with a product name for a clean white-background version.</p>
          </div>
        )}

        {/* ---------- Results ---------- */}
        {results && results.length === 0 && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{emptyMessage}</p>
        )}

        {results && results.length > 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-900">
                {results.length} {results.length === 1 ? "image" : "images"} found{foundIn ? ` in “${foundIn}”` : ""}
              </span>
              <button
                type="button"
                onClick={handleSend}
                disabled={!canSend || selectedCount === 0}
                title={canSend ? "" : "Add the Image Creation component to your Components first"}
                className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send size={13} /> Send to Image Creation
              </button>
            </div>
            {missingNote && <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{missingNote}</p>}
            {!canSend && (
              <p className="text-xs text-gray-500">Add the Image Creation component from the Library to send images there.</p>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {results.map((r, i) => {
                const name = r.label || "Extracted product";
                const ex = r.exact || null;
                const originalSrc = ex?.url || r.originalDataUrl;
                return (
                  <div key={i} className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <label className="flex min-w-0 items-center gap-2 text-sm font-medium text-gray-900">
                        <input
                          type="checkbox"
                          checked={!!selected[i]}
                          disabled={!r.enhancedDataUrl}
                          onChange={(e) => setSelected((prev) => ({ ...prev, [i]: e.target.checked }))}
                        />
                        <span className="truncate">{name}</span>
                      </label>
                      <span className="flex-none text-xs text-gray-500">Page {r.page}</span>
                    </div>

                    {r.enhancedDataUrl ? (
                      <div className="grid grid-cols-2 gap-2">
                        <figure className="flex flex-col gap-1">
                          <div className="grid aspect-square place-items-center overflow-hidden rounded-lg bg-gray-100">
                            <img src={originalSrc} alt={`${name} original`} className="max-h-full max-w-full object-contain" />
                          </div>
                          <figcaption className="text-center text-xs text-gray-500">
                            {ex ? `Original from PDF (${ex.width}×${ex.height})` : "Original (page crop)"}
                          </figcaption>
                        </figure>
                        <figure className="flex flex-col gap-1">
                          <div className="grid aspect-square place-items-center overflow-hidden rounded-lg border border-gray-200 bg-white">
                            <img src={r.enhancedDataUrl} alt={`${name} enhanced`} className="max-h-full max-w-full object-contain" />
                          </div>
                          <figcaption className="text-center text-xs text-gray-500">Enhanced</figcaption>
                        </figure>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {r.originalDataUrl && (
                          <img src={r.originalDataUrl} alt={`${name} original`} className="max-h-40 self-center rounded-lg object-contain" />
                        )}
                        <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{r.error || "Couldn't enhance this image."}</p>
                      </div>
                    )}

                    <div className="flex flex-col gap-1.5">
                      {originalSrc && (
                        <button
                          type="button"
                          onClick={() => downloadDataUrl(originalSrc, `${slugify(name) || "product"}-original.png`)}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                        >
                          <Download size={13} /> {ex ? "Download exact image from PDF" : "Download original (page crop)"}
                        </button>
                      )}
                      {r.enhancedDataUrl && (
                        <button
                          type="button"
                          onClick={() => downloadDataUrl(r.enhancedDataUrl, `${slugify(name) || "product"}-white.png`)}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-gray-400"
                        >
                          <Download size={13} /> Download enhanced (white background)
                        </button>
                      )}
                      {!ex && r.originalDataUrl && (
                        <p className="text-xs text-gray-500">
                          This picture isn't a separate image inside the PDF (it is part of a scanned or flattened page), so the page crop is the closest original.
                        </p>
                      )}
                      {r.enhancedDataUrl && r.verified === "verified" && (
                        <p className="text-xs text-emerald-700">Checked: this is {r.shows ? `${r.shows}` : "the product you named"}.</p>
                      )}
                      {r.enhancedDataUrl && r.verified === "unsure" && (
                        <p className="text-xs text-amber-700">
                          Couldn't fully confirm this is the right product{r.shows ? ` (it looks like ${r.shows})` : ""}. Please check it before using it.
                        </p>
                      )}
                      {r.enhancedDataUrl && r.fallback && (
                        <p className="text-xs text-amber-700">
                          AI enhancement wasn't available for this picture, so the standard enhance was used (cleaner white background and sharper detail).
                        </p>
                      )}
                      {r.enhancedDataUrl && r.fallback && !r.backgroundReplaced && (
                        <p className="text-xs text-gray-500">
                          The background isn't a single solid colour, so it was kept as is — the image was sharpened and cleaned up only.
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
